// src/pages/Admin/PagesPersonnalisees/components/ArticlesManager.jsx

import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  FaCheck,
  FaImage,
  FaPen,
  FaPlus,
  FaSave,
  FaTimes,
  FaTrashAlt,
} from "react-icons/fa";

import {
  buildInitialArticleForm,
  getMediaUrl,
  getPageArticles,
  normalizeArticlePayload,
  validateArticleForm,
} from "../utils/pagesPersonnaliseesUtils";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-cyan-500/10";

const labelClass =
  "mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200";

export default function ArticlesManager({
  page,
  saving = false,
  onAddArticle,
  onUpdateArticle,
  onDeleteArticle,
  onClose,
}) {
  const articles = useMemo(() => getPageArticles(page), [page]);

  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState(() =>
    buildInitialArticleForm(page.id)
  );
  const [deleteCandidateId, setDeleteCandidateId] = useState(null);

  useEffect(() => {
    setEditingArticle(null);
    setDeleteCandidateId(null);
    setFormData(buildInitialArticleForm(page.id));
  }, [page.id]);

  const previewUrl = getMediaUrl(formData.url_image);

  const resetForm = () => {
    setEditingArticle(null);
    setFormData(buildInitialArticleForm(page.id));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData(buildInitialArticleForm(page.id, article));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = normalizeArticlePayload({
      ...formData,
      page_id: page.id,
    });

    const errorMessage = validateArticleForm(payload);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    if (editingArticle) {
      await onUpdateArticle(payload);
    } else {
      await onAddArticle(payload);
    }

    resetForm();
  };

  const handleDelete = async (articleId) => {
    await onDeleteArticle({
      pageId: page.id,
      articleId,
    });

    setDeleteCandidateId(null);

    if (editingArticle?.id === articleId) {
      resetForm();
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Articles de la page
            </h3>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {articles.length} article{articles.length > 1 ? "s" : ""} dans “
              {page.titre}”.
            </p>
          </div>

          {editingArticle && (
            <button
              type="button"
              onClick={resetForm}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Annuler l’édition
            </button>
          )}
        </div>

        {articles.length ? (
          <div className="mt-5 grid gap-4">
            {articles.map((article) => {
              const isDeleting = deleteCandidateId === article.id;
              const imageUrl = getMediaUrl(article.url_image);
              const isEditing = editingArticle?.id === article.id;

              return (
                <article
                  key={article.id}
                  className={`overflow-hidden rounded-3xl border bg-white shadow-sm dark:bg-slate-950 ${
                    isEditing
                      ? "border-cyan-300 ring-4 ring-cyan-100 dark:border-cyan-500/50 dark:ring-cyan-500/10"
                      : "border-slate-200 dark:border-slate-700"
                  }`}
                >
                  <div className="grid gap-0 lg:grid-cols-[240px_minmax(0,1fr)]">
                    <div className="relative min-h-[180px] bg-slate-100 dark:bg-slate-800">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={article.image_alt || article.titre_article}
                          className="h-full min-h-[180px] w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full min-h-[180px] flex-col items-center justify-center text-slate-400">
                          <FaImage className="text-4xl" />
                          <p className="mt-3 text-sm font-medium">
                            Aucun visuel
                          </p>
                        </div>
                      )}

                      <span className="absolute left-3 top-3 rounded-full bg-slate-950/80 px-3 py-1 text-xs font-bold text-white backdrop-blur">
                        Ordre {Number(article.ordre || 0)}
                      </span>
                    </div>

                    <div className="flex flex-col p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <h4 className="text-lg font-bold text-slate-950 dark:text-white">
                            {article.titre_article || "Article sans titre"}
                          </h4>

                          {article.image_alt && (
                            <p className="mt-1 text-xs font-semibold text-slate-400">
                              Alt image : {article.image_alt}
                            </p>
                          )}
                        </div>

                        <div className="flex shrink-0 gap-2">
                          {isDeleting ? (
                            <>
                              <button
                                type="button"
                                disabled={saving}
                                onClick={() => handleDelete(article.id)}
                                className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-rose-700 disabled:opacity-60"
                              >
                                <FaCheck />
                                Oui
                              </button>

                              <button
                                type="button"
                                disabled={saving}
                                onClick={() => setDeleteCandidateId(null)}
                                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                              >
                                <FaTimes />
                                Non
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                disabled={saving}
                                onClick={() => handleEdit(article)}
                                className="grid h-10 w-10 place-items-center rounded-xl border border-orange-100 bg-white text-orange-600 transition hover:bg-orange-50 disabled:opacity-60 dark:border-orange-500/20 dark:bg-slate-950 dark:text-orange-300 dark:hover:bg-orange-500/10"
                                title="Modifier l'article"
                              >
                                <FaPen />
                              </button>

                              <button
                                type="button"
                                disabled={saving}
                                onClick={() => setDeleteCandidateId(article.id)}
                                className="grid h-10 w-10 place-items-center rounded-xl border border-rose-100 bg-white text-rose-600 transition hover:bg-rose-50 disabled:opacity-60 dark:border-rose-500/20 dark:bg-slate-950 dark:text-rose-300 dark:hover:bg-rose-500/10"
                                title="Supprimer l'article"
                              >
                                <FaTrashAlt />
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      <p className="mt-3 whitespace-pre-line text-sm leading-6 text-slate-600 dark:text-slate-300">
                        {article.contenu_texte || "Aucun texte renseigné."}
                      </p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="mt-5 rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-950">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              <FaImage className="text-2xl" />
            </div>

            <h4 className="mt-4 font-bold text-slate-950 dark:text-white">
              Aucun article pour le moment
            </h4>

            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Ajoutez un premier article pour composer cette page personnalisée.
            </p>
          </div>
        )}
      </section>

      <form
        onSubmit={handleSubmit}
        className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900"
      >
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          {editingArticle ? "Modifier l’article" : "Ajouter un article"}
        </h3>

        <div className="mt-4 grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt={formData.image_alt || "Aperçu article"}
                className="h-60 w-full object-cover"
              />
            ) : (
              <div className="flex h-60 flex-col items-center justify-center text-slate-400">
                <FaImage className="text-4xl" />
                <p className="mt-3 text-sm font-medium">Aucun aperçu</p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label className={labelClass}>Titre de l’article</label>
              <input
                type="text"
                name="titre_article"
                value={formData.titre_article}
                onChange={handleChange}
                placeholder="Ex : La naissance du judo"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Contenu texte</label>
              <textarea
                name="contenu_texte"
                value={formData.contenu_texte}
                onChange={handleChange}
                rows={7}
                placeholder="Texte de l’article. Les retours à la ligne seront conservés côté affichage."
                className={inputClass}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_140px]">
              <div>
                <label className={labelClass}>URL de l’image</label>
                <input
                  type="text"
                  name="url_image"
                  value={formData.url_image}
                  onChange={handleChange}
                  placeholder="https://... ou /img/pages/image.jpg"
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Ordre</label>
                <input
                  type="number"
                  name="ordre"
                  value={formData.ordre}
                  onChange={handleChange}
                  min="0"
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Texte alternatif image</label>
              <input
                type="text"
                name="image_alt"
                value={formData.image_alt}
                onChange={handleChange}
                placeholder="Ex : Portrait de Jigoro Kano"
                className={inputClass}
              />
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={resetForm}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Réinitialiser
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            {saving ? <FaSave /> : editingArticle ? <FaSave /> : <FaPlus />}
            {saving
              ? "Enregistrement..."
              : editingArticle
                ? "Enregistrer l’article"
                : "Ajouter l’article"}
          </button>
        </div>
      </form>

      <div className="sticky bottom-0 -mx-5 border-t border-slate-200 bg-white/90 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}